import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import FormInput from '../../../components/FormInput';
import RolesDropdown from '../../../components/deac/RolesDropdown';
import { useAuth } from '../../../context/Auth';
import Toast from 'react-native-toast-message';
import { deacService, HasOpenVacancies } from '../../../services/central-api/deac';
import { AlreadyExists } from '../../../services/central-api/base-service';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedVacancyLevelId: number | undefined;
    onConfirm: () => void;
}

export default function VacancyLevelModal({ selectedVacancyLevelId, isModalVisible, setModalVisible, onConfirm }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [name, setName] = useState<string>('');
    const [rolesIds, setRolesIds] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFetchingData, setIsFetchingData] = useState<boolean>(selectedVacancyLevelId && selectedVacancyLevelId > 0 ? true : false);

    async function getVacancyLevel() {
        try {
            setIsFetchingData(true);
            const vacancyLevel = await deacService.getVacancyLevelById(Number(selectedVacancyLevelId));
            setName(vacancyLevel.name);
            setRolesIds(vacancyLevel.roles?.map(r => r.id) ?? []);
            setActingBodyId(vacancyLevel.actingBody.id);
        } catch (err) {
            if (err instanceof HasOpenVacancies) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('vacancyLevelHasOpenVacancies'),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            closeModal();
        } finally {
            setIsFetchingData(false);
        }
    }

    async function formSubmit() {
        try {
            setIsLoading(true);

            if (selectedVacancyLevelId) {
                await deacService.updateVacancyLevel({ id: selectedVacancyLevelId, name, rolesIds });
            } else {
                await deacService.createVacancyLevel({ name, rolesIds, actingBodyId });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            closeModal();
            onConfirm();
        } catch (err) {
            if (err instanceof AlreadyExists) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('vacancyLevelAlreadyExist'),
                });
            } else if (err instanceof HasOpenVacancies) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('vacancyLevelHasOpenVacancies'),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            if (selectedVacancyLevelId) {
                getVacancyLevel();
            } else if (!authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancy_level) {
                setActingBodyId(authenticatedUser?.actingBodyUnit.actingBodyId ?? 0);
            }
        }
    }, [selectedVacancyLevelId, isModalVisible]);

    function isFormValid(): boolean {
        if (name == '') {
            return false;
        }

        if (authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancy_level && actingBodyId == 0) {
            return false;
        }

        return true;
    }

    const closeModal = () => {
        setModalVisible(false);
        setName('');
        setRolesIds([]);
        setActingBodyId(0);
    };

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    {isFetchingData ?
                        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                            <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                        </View>
                        : <>
                            <View style={{ flexDirection: 'column', rowGap: 8 }}>
                                <MyAppText style={styles.header}>{translate('levelRegistration')}</MyAppText>
                                <MyAppText style={styles.subheader}>{translate('fillFieldsBelow')}</MyAppText>
                            </View>
                            <View style={{ flexDirection: 'column', rowGap: 16 }}>
                                <ActingBodiesDropdown
                                    value={actingBodyId}
                                    setValue={setActingBodyId}
                                    includeAllOption={false}
                                    labelStyle={styles.inputLabelStyle}
                                    disabled={selectedVacancyLevelId != undefined || !authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancy_level}
                                    zIndex={2}
                                />

                                <FormInput
                                    labelStyle={styles.inputLabelStyle}
                                    onChangeText={(value) => setName(value)}
                                    value={name}
                                    label={translate('title')}
                                />
                                <RolesDropdown
                                    actingBodyId={actingBodyId}
                                    zIndex={1}
                                    setValue={setRolesIds}
                                    value={rolesIds}
                                />
                            </View>
                            <FormActions
                                viewStyle={{ flex: 1, justifyContent: 'space-around' }}
                                buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                                buttonContentContainerStyle={{ flex: 1 }}
                                onSubmit={formSubmit}
                                onClose={closeModal}
                                disabled={!isFormValid() || isLoading}
                                isLoading={isLoading}
                            />
                        </>
                    }
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
}));
